import { OptionList, TextAnswer } from "./Inputs";
import { GappedPassage, NoticeCard, ChoiceDirectory, Passage } from "./PartContext";
import { ListeningPlayer } from "./ListeningPlayer";

const TEXT_TYPES = ["open_cloze", "notes"];
const GAP_TYPES = ["gapped", "cloze", "open_cloze", "notes"];

function promptFor(part, q) {
  switch (part.type) {
    case "signs": return "What does it say?";
    case "matching": return q.prompt;
    case "gapped": return `Which sentence fits gap ${q.number}?`;
    case "cloze": return `Choose the best word for gap ${q.number}.`;
    case "open_cloze": return `Write ONE word for gap ${q.number}.`;
    case "notes": return `Complete gap ${q.number}.`;
    default: return q.prompt;
  }
}

function optionsFor(part, q) {
  if (part.type === "matching") return part.choices.map((c) => c.title);
  if (part.type === "gapped") return part.choices.map((c) => c.text);
  return q.options;
}

function Context({ part, q, answers }) {
  if (part.type === "signs") return <NoticeCard q={q} />;
  if (part.type === "matching") return <ChoiceDirectory choices={part.choices} />;
  if (GAP_TYPES.includes(part.type)) return <GappedPassage part={part} currentNumber={q.number} answers={answers} />;
  if (part.passage) return <Passage part={part} />;
  return null;
}

export const PartStep = ({ step, answers, setAnswer, plays, onPlayed }) => {
  const { part, q, section, partNo } = step;
  const value = answers[q.id];
  const listening = section === "listening";
  const audioKey = part.type === "clips" ? q.id : part.id;
  const context = <Context part={part} q={q} answers={answers} />;
  const hasContext = part.type !== "clips" && part.type !== "interview";

  const player = listening && (
    <ListeningPlayer
      key={audioKey}
      script={q.script || part.script}
      audioUrl={part.type === "clips" ? q.audio_url : part.audio_url}
      context={q.context || part.context}
      playsUsed={plays[audioKey] || 0}
      onPlayed={() => onPlayed(audioKey)}
    />
  );

  return (
    <div>
      <div className="mb-6">
        <div className="text-xs font-mono-fig text-muted-foreground" data-testid="test-part-label">
          Part {partNo} · {part.title} · {q.difficulty || part.difficulty}
        </div>
        <p className="mt-2 text-sm text-foreground/75 max-w-3xl">{part.instructions}</p>
      </div>
      <div className={hasContext ? "grid lg:grid-cols-2 gap-8" : "max-w-3xl"}>
        {hasContext ? (
          <div className="lg:sticky lg:top-24 self-start space-y-5">
            {player}
            <div className="neu-inset rounded-2xl p-6">{context}</div>
          </div>
        ) : player}
        <div className={hasContext ? "" : "mt-8"}>
          <div className="overline">Question {q.number}</div>
          <h2 className="text-xl md:text-2xl mt-2 text-foreground font-bold leading-snug" data-testid="test-question-prompt">
            {promptFor(part, q)}
          </h2>
          {TEXT_TYPES.includes(part.type) ? (
            <TextAnswer key={q.id} value={value} number={q.number} onChange={(v) => setAnswer(q.id, v)} />
          ) : (
            <OptionList options={optionsFor(part, q)} value={value} onChange={(v) => setAnswer(q.id, v)} testIdPrefix={`test-${section}`} />
          )}
        </div>
      </div>
    </div>
  );
};
